import type { VariantProps } from 'cva'

import { cva } from '~/utils/classnames'

import { Slot } from './slot'

const badgeStyle = cva({
  base: 'text-2xs inline-flex shrink-0 items-center gap-1 rounded-md px-[0.4em] py-[0.25em] leading-none font-semibold',
  variants: {
    variant: {
      default: 'bg-foreground/6 text-foreground',
      highlight: 'bg-accent-primary text-accent-foreground',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

interface BadgeProps extends React.ComponentPropsWithRef<'div'> {
  asChild?: boolean
  variant?: VariantProps<typeof badgeStyle>['variant']
}

const BadgeRoot = ({
  ref,
  children,
  variant = 'default',
  className,
  asChild,
  ...rest
}: BadgeProps) => {
  const Comp = asChild ? Slot : 'div'

  return (
    <Comp ref={ref} className={badgeStyle({ variant, className })} {...rest}>
      {children}
    </Comp>
  )
}

const badgeStatusVariants = cva({
  base: 'mx-0.5 size-1.5 rounded-full bg-current first:ml-0 last:mr-0',
  variants: {
    status: {
      neutral: 'bg-gray-500',
      success: 'bg-positive',
      warning: 'bg-warning',
      error: 'bg-destructive',
    },
  },
  defaultVariants: {
    status: 'neutral',
  },
})

type BadgeStatusProps = Omit<React.ComponentPropsWithRef<'div'>, 'children'> & {
  status?: VariantProps<typeof badgeStatusVariants>['status']
}

const BadgeStatus = ({ className, status, ...rest }: BadgeStatusProps) => {
  return (
    <div className={badgeStatusVariants({ status, className })} {...rest} />
  )
}

const Badge = Object.assign(BadgeRoot, {
  Status: BadgeStatus,
})

export { Badge }
